// src/components/HomeDropdown.js
import React, { useState, useRef, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { FaUserCircle } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import './HomeDropdown.css';

import { TabContext } from '../context/TabContext';
import { ModalContext } from '../context/ModalContext';
import ModalSignIn from './ModalSignIn';
import ModalUserDetail from './ModalUserDetail';

import useCloseOnScroll from '../hooks/useCloseOnScroll';

const HomeDropdown = ({ isAuthenticated, logout }) => {
  const { activeTab } = useContext(TabContext);
  const { openModal, modalStacks } = useContext(ModalContext);

  const [dropdownOpen, setDropdownOpen]       = useState(false);
  const dropdownRef                           = useRef(null);
  const [menuStyle, setMenuStyle]             = useState({ top: 0, left: 0 });
  const [scrollContainer, setScrollContainer] = useState(null);

  // ─── [A] Detectamos el VERDADERO contenedor scrollable ─────────────
  useEffect(() => {
    // Recorremos todos los DIVs, buscando overflowY auto/scroll real
    const allDivs = Array.from(document.querySelectorAll('div'));
    let container = window; // fallback
    for (let div of allDivs) {
      const style = window.getComputedStyle(div);
      if (
        (style.overflowY === 'auto' || style.overflowY === 'scroll') &&
        div.scrollHeight > div.clientHeight
      ) {
        container = div;
        break;
      }
    }
    //console.log('[HomeDropdown] [A] activeTab=', activeTab, '→ scrollContainer detectado:', container === window ? 'window' : container);
    setScrollContainer(container);
  }, [activeTab]);

  // ─── [B] Hook que cierra el dropdown al hacer scroll en ese contenedor ─
  useCloseOnScroll(
    () => {

      if (dropdownOpen) {
        //console.log('[HomeDropdown] [B] cierro dropdown por scroll');
        setDropdownOpen(false);
      }
    },
    scrollContainer
  );

  // ─── Resto de tu lógica sin cambios, con logs mínimos ─────────────────

  const toggleDropdown = () => {
    setDropdownOpen(o => {
      //console.log('[HomeDropdown] toggleDropdown →', !o);
      return !o;
    });
  };

  // Calcula posición del menú
  useEffect(() => {
    if (dropdownOpen && dropdownRef.current) {
      const r = dropdownRef.current.getBoundingClientRect();
      const top  = r.bottom + window.scrollY;
      const left = r.left   - 150 + window.scrollX;
      setMenuStyle({ top, left });
      //console.log('[HomeDropdown] [C] menuStyle=', { top, left });
    }
  }, [dropdownOpen]);

  // Cierra al click fuera
  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        //console.log('[HomeDropdown] [D] click fuera, cierro dropdown');
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const menu = (
    <div
      className={`dropdown-menu ${dropdownOpen ? 'show' : ''}`}
      style={{ ...menuStyle, position: 'absolute', zIndex: 20000 }}
    >
      {/* Si el usuario NO está autenticado, mostramos la opción de Inicio/Registro */}
      {!isAuthenticated ? (
        <button
          className="dropdown-item"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            setDropdownOpen(false);

            // ─── 1️⃣ Comprobar en TODAS las pilas si ya hay “signIn” ─────────
            const yaSignIn = Object.values(modalStacks).some(stack =>
              stack.some(modal => modal.metadata?.modalType === 'signIn')
            );
            if (yaSignIn) {
              // Si ya existe el modal de Inicio/Registro en cualquier pestaña, no abrimos otro
              return;
            }

            // ─── 2️⃣ Abrimos modal de Inicio/Registro ────────────────────────
            openModal(
              <ModalSignIn />,
              {
                tab: activeTab,
                metadata: { modalType: 'signIn', origin: 'HomeDropdown' },
                overlayClassName: 'pet-detail-overlay',
              }
            );
          }}
        >
          Inicio/Registro
        </button>
      ) : (
        <button
          className="dropdown-item"
          onMouseDown={e => e.stopPropagation()}
          onClick={e => {
            e.stopPropagation();
            setDropdownOpen(false);

            // ─── 1️⃣ Comprobar en TODAS las pilas si ya hay “userDetail” ─────────
            const yaUserDetail = Object.values(modalStacks).some(stack =>
              stack.some(modal => modal.metadata?.modalType === 'userDetail')
            );
            if (yaUserDetail) {
              return;
            }

            // ─── 2️⃣ Si no existe, abrimos el modal para la pestaña activa ─────────
            openModal(
              <ModalUserDetail />,
              {
                tab: activeTab,
                overlayClassName: 'pet-detail-overlay',
                metadata: {
                  modalType: 'userDetail',
                  modalMode: 'view'
                }
              }
            );
          }}
        >
          Mi perfil
        </button>
      )}
      <Link onClick={() => setDropdownOpen(false)} to="/terms" className="dropdown-item">
        Términos y condiciones
      </Link>
      <Link onClick={() => setDropdownOpen(false)} to="/privacy" className="dropdown-item">
        Política de privacidad
      </Link>
      <Link onClick={() => setDropdownOpen(false)} to="/help" className="dropdown-item">
        Ayuda
      </Link>
      {isAuthenticated && (
        <button
          onMouseDown={(e) => {
            // Detenemos la propagación al momento de "presionar" el botón
            e.stopPropagation();
            //console.log('[HomeDropdown] Clic en Inicio/Registro - onMouseDown');
          }}
          onClick={(e) => {
            e.stopPropagation();
            logout();
            setDropdownOpen(false);
          }}
          className="dropdown-item"
        >
          Cerrar sesión
        </button>
      )}
    </div>
  );

  return (
    <div ref={dropdownRef} className="dropdown">
      <button
        className="btn btn-link dropdown-toggle"
        type="button"
        onClick={toggleDropdown}
        aria-expanded={dropdownOpen}
      >
        <FaUserCircle
          className="user-icon"
          style={{ color: isAuthenticated ? '#0093F5' : '#d3d3d3' }}
        />
      </button>
      {dropdownOpen && ReactDOM.createPortal(menu, document.body)}
    </div>
  );
};

export default HomeDropdown;
